import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Page, Stepper } from '../components/Layout'
import { Aviso, ErrorBox, Field, Money, TextInput } from '../components/ui'
import { apiPost } from '../lib/api'
import { getFunnel, setFunnel } from '../lib/funnel'

interface TriagemResp {
  triagem_id: string
  viavel: boolean
  motivo: string | null
  score: number
  atraso_meses: number
  estimativa_ilustrativa: { min: number; max: number; aviso: string }
}

export default function Triagem() {
  const nav = useNavigate()
  const f = getFunnel()
  const [erro, setErro] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resultado, setResultado] = useState<TriagemResp | null>(null)

  // contato
  const [nome, setNome] = useState(f.nome ?? '')
  const [email, setEmail] = useState(f.email ?? '')
  const [telefone, setTelefone] = useState(f.telefone ?? '')
  // questionário
  const [temContrato, setTemContrato] = useState(true)
  const [dataPrometida, setDataPrometida] = useState(f.data_entrega_prometida ?? '')
  const [jaEntregue, setJaEntregue] = useState(false)
  const [dataReal, setDataReal] = useState('')
  const [valorParcela, setValorParcela] = useState(f.valor_parcela ? String(f.valor_parcela) : '')
  const [houveDistrato, setHouveDistrato] = useState('')
  const [cobrancaIndevida, setCobrancaIndevida] = useState('')
  // enquadramento (define competência e tipo de ação na inicial)
  const [financiadoCaixa, setFinanciadoCaixa] = useState('')
  const [objetivo, setObjetivo] = useState('')

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setLoading(true)
    try {
      const lead = await apiPost<{ lead_id: string }>('/api/leads/submit', {
        nome,
        email,
        telefone,
        origem: f.origem ?? 'triagem',
        payload: f.payload,
      })
      const parcela = Number(valorParcela.replace(',', '.')) || undefined
      const resp = await apiPost<TriagemResp>('/api/triagem/submit', {
        lead_id: lead.lead_id,
        respostas: {
          tem_contrato: temContrato,
          data_entrega_prometida: dataPrometida || undefined,
          ja_entregue: jaEntregue,
          data_entrega_real: jaEntregue ? dataReal || undefined : undefined,
          valor_parcela: parcela,
          houve_distrato: houveDistrato === 'sim' ? true : houveDistrato === 'nao' ? false : undefined,
          cobranca_indevida: cobrancaIndevida === 'sim' ? true : cobrancaIndevida === 'nao' ? false : undefined,
          financiado_caixa: financiadoCaixa === 'sim' ? true : financiadoCaixa === 'nao' ? false : undefined,
          objetivo: objetivo === 'rescindir' || objetivo === 'manter' ? objetivo : undefined,
        },
      })
      setFunnel({
        lead_id: lead.lead_id,
        triagem_id: resp.triagem_id,
        nome,
        email,
        telefone,
        valor_parcela: parcela,
        data_entrega_prometida: dataPrometida || undefined,
        estimativa: { min: resp.estimativa_ilustrativa.min, max: resp.estimativa_ilustrativa.max },
      })
      setResultado(resp)
    } catch (e2) {
      setErro((e2 as Error).message)
    } finally {
      setLoading(false)
    }
  }

  if (resultado) {
    return (
      <Page>
        <Stepper step={0} />
        {resultado.viavel ? (
          <div className="card">
            <h1 className="text-2xl font-semibold text-pfa-navy">Seu caso parece elegível</h1>
            <p className="mt-2 text-pfa-slate">
              Identificamos atraso de aproximadamente <strong>{resultado.atraso_meses} meses</strong> além do prazo de
              tolerância. Faixa ilustrativa estimada:
            </p>
            <p className="mt-3 text-2xl">
              <Money value={resultado.estimativa_ilustrativa.min} /> <span className="text-pfa-slate">a</span>{' '}
              <Money value={resultado.estimativa_ilustrativa.max} />
            </p>
            <div className="mt-4">
              <Aviso>{resultado.estimativa_ilustrativa.aviso}</Aviso>
            </div>
            <button className="btn-accent mt-6" onClick={() => nav('/proposta')}>
              Ver proposta
            </button>
          </div>
        ) : (
          <div className="card">
            <h1 className="text-2xl font-semibold text-pfa-navy">Avaliação inicial</h1>
            <p className="mt-2 text-pfa-slate">{resultado.motivo ?? 'Seu caso não se enquadrou nos critérios desta triagem inicial.'}</p>
            <p className="mt-2 text-sm text-pfa-slate/80">
              Isso não é uma decisão definitiva. Se quiser, fale com a equipe para uma análise mais detalhada.
            </p>
          </div>
        )}
      </Page>
    )
  }

  return (
    <Page>
      <Stepper step={0} />
      <h1 className="text-2xl font-semibold text-pfa-navy">Triagem do seu caso</h1>
      <p className="mt-2 max-w-2xl text-pfa-slate">Algumas perguntas para entender seu caso. Leva 2 minutos.</p>

      <form onSubmit={enviar} className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="card">
          <h2 className="mb-4 font-semibold text-pfa-navy">Contato</h2>
          <Field label="Nome">
            <TextInput value={nome} onChange={(e) => setNome(e.target.value)} required />
          </Field>
          <Field label="E-mail">
            <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Telefone / WhatsApp">
            <TextInput value={telefone} onChange={(e) => setTelefone(e.target.value)} />
          </Field>
        </div>

        <div className="card">
          <h2 className="mb-4 font-semibold text-pfa-navy">Sobre o imóvel</h2>
          <label className="mb-3 flex items-center gap-2 text-sm text-pfa-slate">
            <input type="checkbox" checked={temContrato} onChange={(e) => setTemContrato(e.target.checked)} />
            Tenho contrato de compra/incorporação
          </label>
          <Field label="Data de entrega prometida no contrato">
            <TextInput type="date" value={dataPrometida} onChange={(e) => setDataPrometida(e.target.value)} />
          </Field>
          <label className="mb-3 flex items-center gap-2 text-sm text-pfa-slate">
            <input type="checkbox" checked={jaEntregue} onChange={(e) => setJaEntregue(e.target.checked)} />
            O imóvel já foi entregue
          </label>
          {jaEntregue && (
            <Field label="Data da entrega real">
              <TextInput type="date" value={dataReal} onChange={(e) => setDataReal(e.target.value)} />
            </Field>
          )}
          <Field label="Valor da parcela mensal (R$)" hint="Usado para calcular a entrada (equivale a 3× a parcela).">
            <TextInput inputMode="decimal" value={valorParcela} onChange={(e) => setValorParcela(e.target.value)} />
          </Field>
          <Field label="O imóvel é financiado pela Caixa (ou é Minha Casa Minha Vida)?">
            <select className="input" value={financiadoCaixa} onChange={(e) => setFinanciadoCaixa(e.target.value)}>
              <option value="">Selecione…</option>
              <option value="sim">Sim, financiado pela Caixa / é MCMV</option>
              <option value="nao">Não, é financiamento de outro banco ou à vista</option>
              <option value="nao_sei">Não tenho certeza</option>
            </select>
          </Field>
          <Field label="Qual o seu objetivo?">
            <select className="input" value={objetivo} onChange={(e) => setObjetivo(e.target.value)}>
              <option value="">Selecione…</option>
              <option value="rescindir">Receber meu dinheiro de volta (rescindir o contrato)</option>
              <option value="manter">Manter o imóvel e ser indenizado pelo atraso</option>
            </select>
          </Field>
          <Field label="Houve cobrança que você considera indevida?">
            <select className="input" value={cobrancaIndevida} onChange={(e) => setCobrancaIndevida(e.target.value)}>
              <option value="">Selecione…</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </Field>
          <Field label="Houve pedido de distrato?">
            <select className="input" value={houveDistrato} onChange={(e) => setHouveDistrato(e.target.value)}>
              <option value="">Selecione…</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </Field>
        </div>

        <div className="md:col-span-2">
          <ErrorBox message={erro} />
          <button className="btn-accent mt-2" disabled={loading} type="submit">
            {loading ? 'Avaliando…' : 'Avaliar meu caso'}
          </button>
        </div>
      </form>
    </Page>
  )
}
